import { useCallback } from "react";
import { CompanyEntryDto } from "@advanced-sales-technologies/backend-client/models/CompanyEntryDto";
import { SelectOptionRequestDto } from "@advanced-sales-technologies/backend-client/models/SelectOptionRequestDto";
import type { SimpleSelectOptionDto } from "@advanced-sales-technologies/backend-client/models/SimpleSelectOptionDto";
import { CellContext } from "@tanstack/table-core/build/lib/core/cell";
import clsx from "clsx";

import { useGetOfficePhoneOptions } from "@/hooks/api/list/options/useGetOfficePhoneOptions";
import HasMoreDataCellGeneral from "@/components/molecules/table/HasMoreDataCellGeneral";
import { searchColumns, SearchColumnsProps } from "@/components/organisms/search/searchColumns";

type OfficeKey = "office" | "office_hq";

type Props = {
  info: CellContext<CompanyEntryDto, unknown>;
  officeKey: OfficeKey;
} & SearchColumnsProps;

const HasMoreOfficePhoneCell = ({ t, listId, searchParams, queryKey, info, officeKey }: Props) => {
  const company = info.row.original;
  const companyId = company.id;
  const office = company[officeKey];

  if (!office || !office.contact_info || !office.contact_info.phone) return null;

  const buildDisplayValueCb = useCallback((option: SimpleSelectOptionDto) => option.value || "", []);

  const useQueryHookCb = useCallback(
    (query: string | null, enabled: boolean, searchParamsArgs: Record<string, unknown>) =>
      useGetOfficePhoneOptions(
        {
          id: office.id,
          companyId: companyId,
          option: {
            query: query,
            list_id: listId,
            configuration: searchParamsArgs,
          },
        },
        enabled,
      ),
    [office.id, companyId, listId, useGetOfficePhoneOptions],
  );

  const renderValueCb = useCallback((value: string | null | undefined) => {
    const phone = value?.replace(/\s+/g, "");
    return (
      <a href={`tel:${phone}`} target="_blank" rel="noreferrer" className={"text-link-gray underline"}>
        {value}
      </a>
    );
  }, []);

  const renderOptionCb = useCallback(
    (option: SimpleSelectOptionDto, isSelected: boolean) => (
      <span
        key={option.id}
        className={"flex flex-1 flex-col gap-x-1.5 truncate py-2 text-left text-sm text-quaternary"}
      >
        {option.value && (
          <div className={clsx("truncate font-medium", isSelected ? "text-primary" : "text-secondary")}>
            {option.value}
          </div>
        )}
      </span>
    ),
    [],
  );

  return (
    <HasMoreDataCellGeneral<SimpleSelectOptionDto>
      queryKey={queryKey}
      company={company}
      buildDisplayValue={buildDisplayValueCb}
      searchParams={searchParams}
      displayValue={office.contact_info.phone.value}
      initialSelected={office.contact_info.phone.id}
      listId={listId}
      target={SelectOptionRequestDto.target.OFFICE_PHONE}
      hasMoreData={office.contact_info.phone.has_more}
      placeholder={searchColumns(t).contact_info_phone.name}
      useQueryHook={useQueryHookCb}
      renderValue={renderValueCb}
      renderOption={renderOptionCb}
    />
  );
};

export default HasMoreOfficePhoneCell;
